import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, X, ChevronDown, ChevronUp, ExternalLink,
  Key, Link2, Users, History, FolderOpen, Camera,
  Palette, BarChart3, MessageSquare, Trash2, Eye, EyeOff
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

import API from '../utils/api';

const TYPE_ICONS = {
  "Credentials & Access": Key,
  "Social Media Links": Link2,
  "Team Allocation": Users,
  "Team History": History,
  "Content & Assets": FolderOpen,
  "Shoot Log": Camera,
  "Branding Files": Palette,
  "Performance / Ads Resources": BarChart3,
  "Client Feedback / Communication": MessageSquare,
};

const TYPE_COLORS = {
  "Credentials & Access": "text-rose-400",
  "Social Media Links": "text-cyan-400",
  "Team Allocation": "text-emerald-400",
  "Team History": "text-slate-400",
  "Content & Assets": "text-amber-400",
  "Shoot Log": "text-purple-400",
  "Branding Files": "text-pink-400",
  "Performance / Ads Resources": "text-indigo-400",
  "Client Feedback / Communication": "text-teal-400",
};

// Human-readable labels for credential fields
const FIELD_LABELS = {
  username: "Username", password: "Password", two_fa_method: "2FA Method",
  page_name: "Page Name", master_account_email: "Master Account Email",
  master_account_password: "Master Account Password", page_role: "Page Role",
  channel_url: "Channel URL", google_account_email: "Google Account Email",
  google_account_password: "Google Account Password",
  associated_email: "Associated Email",
  business_manager_id: "Business Manager ID", account_email: "Account Email",
  ad_account_id: "Ad Account ID", customer_id: "Customer ID (CID)",
  site_url: "Website URL", login_url: "Login URL", hosting_provider: "Hosting Provider",
};

const SENSITIVE_FIELDS = new Set(["password", "master_account_password", "google_account_password"]);

export default function ClientMasterSheet({ clientId, canWrite = true }) {
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [filter, setFilter] = useState('all');
  const [expandedTypes, setExpandedTypes] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, metaRes, empRes] = await Promise.all([
        axios.get(`${API}/client-entries/${clientId}`, { withCredentials: true }),
        axios.get(`${API}/client-entries/meta`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
      ]);
      setEntries(entriesRes.data);
      setMeta(metaRes.data);
      setEmployees(empRes.data.filter(e => e.status === 'Active'));
      const typesWithEntries = new Set(entriesRes.data.map(e => e.entry_type));
      setExpandedTypes(typesWithEntries);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/client-entries/${entryId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const toggleType = (type) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const grouped = {};
  const displayTypes = meta?.entry_types || [];
  for (const type of displayTypes) grouped[type] = entries.filter(e => e.entry_type === type);

  const filteredTypes = filter === 'all' ? displayTypes : displayTypes.filter(t => t === filter);

  if (loading) return <p className="text-slate-500 text-sm py-4">Loading master sheet...</p>;

  return (
    <div data-testid="client-master-sheet">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-8 text-xs w-[200px]" data-testid="master-sheet-filter">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 z-[200] max-h-[300px]">
              <SelectItem value="all">All Types</SelectItem>
              {displayTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500">{entries.length} entries</span>
        </div>
        {canWrite && (
          <Button size="sm" onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="add-entry-button">
            <Plus size={14} className="mr-1" /> Add Entry
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {filteredTypes.map(type => {
          const typeEntries = grouped[type] || [];
          const Icon = TYPE_ICONS[type] || FolderOpen;
          const colorClass = TYPE_COLORS[type] || "text-slate-400";
          const isExpanded = expandedTypes.has(type);

          return (
            <div key={type} className="glass-card overflow-hidden" data-testid={`section-${type.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
              <button className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors" onClick={() => toggleType(type)}>
                <div className="flex items-center gap-2">
                  <Icon size={14} className={colorClass} />
                  <span className="text-sm font-medium text-white">{type}</span>
                  <span className="text-xs bg-white/10 px-1.5 py-0.5 rounded-full text-slate-400">{typeEntries.length}</span>
                </div>
                {isExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
              </button>
              {isExpanded && (
                <div className="border-t border-white/5 px-3 pb-3">
                  {typeEntries.length === 0 ? (
                    <p className="text-xs text-slate-600 py-3 text-center">No entries yet</p>
                  ) : (
                    <div className="space-y-2 pt-2">
                      {typeEntries.map(entry => (
                        <EntryRow key={entry.id} entry={entry} type={type} meta={meta} onDelete={canWrite ? handleDelete : null} formatDate={formatDate} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showAddDialog && (
        <AddEntryDialog clientId={clientId} meta={meta} employees={employees} onClose={() => setShowAddDialog(false)} onAdded={() => { setShowAddDialog(false); fetchData(); }} />
      )}
    </div>
  );
}

// ================================
// ENTRY ROW
// ================================
function EntryRow({ entry, type, meta, onDelete, formatDate }) {
  const [showPasswords, setShowPasswords] = useState(false);

  const credentialFields = meta?.credential_fields?.[entry.platform] || [];

  return (
    <div className="py-2.5 px-3 rounded-lg bg-white/[0.02] border border-white/5 group" data-testid={`entry-${entry.id}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-white font-medium">{entry.title}</span>
          {entry.date && <span className="text-[10px] text-slate-600">{formatDate(entry.date)}</span>}
        </div>
        <div className="flex items-center gap-1">
          {type === "Credentials & Access" && (
            <button onClick={() => setShowPasswords(!showPasswords)} className="text-slate-500 hover:text-slate-300 p-1">
              {showPasswords ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(entry.id)} className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity p-1">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Credentials — platform-specific fields */}
      {type === "Credentials & Access" && (
        <div className="space-y-1 mt-1.5">
          {entry.platform && <div className="text-xs"><span className="text-slate-500">Platform:</span> <span className="text-slate-300">{entry.platform}</span></div>}
          {credentialFields.map(field => {
            const val = entry[field];
            if (!val) return null;
            const isSensitive = SENSITIVE_FIELDS.has(field);
            return (
              <div key={field} className="text-xs">
                <span className="text-slate-500">{FIELD_LABELS[field] || field}:</span>{' '}
                <span className={`${isSensitive ? 'font-mono' : ''} text-slate-300`}>
                  {isSensitive && !showPasswords ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : val}
                </span>
              </div>
            );
          })}
          {entry.access_type && (
            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded mt-1 ${entry.access_type === 'Admin' ? 'bg-rose-500/10 text-rose-400' : entry.access_type === 'Editor' ? 'bg-amber-500/10 text-amber-400' : 'bg-slate-500/10 text-slate-400'}`}>{entry.access_type}</span>
          )}
        </div>
      )}

      {/* Social Links */}
      {type === "Social Media Links" && entry.platform && (
        <span className="text-xs text-slate-500">{entry.platform}</span>
      )}

      {/* Team */}
      {(type === "Team Allocation" || type === "Team History") && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-400 mt-1">
          {entry.role && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">{entry.role}</span>}
          {entry.employee_id && <span className="font-mono text-cyan-400 text-[10px]">{entry.employee_id}</span>}
          {entry.start_date && <span>From: {formatDate(entry.start_date)}</span>}
          {entry.end_date && <span>To: {formatDate(entry.end_date)}</span>}
        </div>
      )}

      {/* Shoot Log */}
      {type === "Shoot Log" && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-400 mt-1">
          {entry.shoot_date && <span>Date: {formatDate(entry.shoot_date)}</span>}
          {entry.shoot_location && <span>Location: <span className="text-slate-300">{entry.shoot_location}</span></span>}
        </div>
      )}

      {/* Links */}
      {entry.link && (
        <a href={entry.link} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-1 truncate">
          {entry.link} <ExternalLink size={10} />
        </a>
      )}
      {entry.raw_content_link && type === "Shoot Log" && (
        <a href={entry.raw_content_link} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-1 truncate">
          Raw Content <ExternalLink size={10} />
        </a>
      )}
      {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
    </div>
  );
}

// ================================
// ADD ENTRY DIALOG
// ================================
function AddEntryDialog({ clientId, meta, employees, onClose, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entryType, setEntryType] = useState('');
  const [credPlatform, setCredPlatform] = useState('');
  const [form, setForm] = useState({});

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.employee_id === empId);
    if (emp) setForm(prev => ({ ...prev, employee_id: empId, employee_name: emp.name, title: emp.name }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = {
        client_id: clientId,
        entry_type: entryType,
        title: form.title || credPlatform || '',
        date: form.date || new Date().toISOString().slice(0, 10),
        ...form,
      };
      if (entryType === "Credentials & Access") {
        payload.platform = credPlatform;
        if (!payload.title || payload.title === '') payload.title = credPlatform;
      }
      if (entryType === "Social Media Links") {
        payload.platform = form.platform;
        if (!payload.title) payload.title = form.platform;
      }

      await axios.post(`${API}/client-entries`, payload, { withCredentials: true });
      toast.success('Entry added');
      onAdded();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail || 'Failed');
    } finally { setLoading(false); }
  };

  const canSubmit = entryType && (form.title || credPlatform || form.platform);
  const credFields = meta?.credential_fields?.[credPlatform] || [];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-lg">Add Entry</DialogTitle>
        </DialogHeader>

        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}

        <div className="space-y-4 py-4">
          {/* Type Selection */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Type *</label>
            <Select value={entryType} onValueChange={(v) => { setEntryType(v); setForm({}); setCredPlatform(''); }}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="entry-type-select"><SelectValue placeholder="Select Type" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200] max-h-[300px]">
                {meta?.entry_types?.filter(t => t !== "Team History").map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* === CREDENTIALS & ACCESS === */}
          {entryType === "Credentials & Access" && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Platform *</label>
                <Select value={credPlatform} onValueChange={(v) => { setCredPlatform(v); setForm({}); }}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="credential-platform"><SelectValue placeholder="Select Platform" /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                    {meta?.credential_platforms?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {credPlatform && credFields.map(field => (
                <div key={field}>
                  <label className="text-xs text-slate-500 mb-1 block">{FIELD_LABELS[field] || field}</label>
                  <Input
                    type={SENSITIVE_FIELDS.has(field) ? "password" : "text"}
                    value={form[field] || ''}
                    onChange={(e) => set(field, e.target.value)}
                    placeholder={FIELD_LABELS[field] || field}
                    className="bg-black/40 border-white/10 text-white h-9 text-sm"
                    data-testid={`cred-${field}`}
                  />
                </div>
              ))}
              {credPlatform && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Access Type</label>
                  <Select value={form.access_type || ''} onValueChange={(v) => set('access_type', v)}>
                    <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="credential-access"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                      {meta?.access_types?.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {credPlatform && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Link</label>
                  <Input value={form.link || ''} onChange={(e) => set('link', e.target.value)} placeholder="https://..." className="bg-black/40 border-white/10 text-white h-9 text-sm" />
                </div>
              )}
            </>
          )}

          {/* === SOCIAL MEDIA LINKS === */}
          {entryType === "Social Media Links" && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Platform *</label>
                <Select value={form.platform || ''} onValueChange={(v) => set('platform', v)}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="social-platform"><SelectValue placeholder="Select Platform" /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                    {meta?.social_platforms?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Link *</label>
                <Input value={form.link || ''} onChange={(e) => set('link', e.target.value)} placeholder="https://instagram.com/..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="social-link" />
              </div>
            </>
          )}

          {/* === TEAM ALLOCATION === */}
          {entryType === "Team Allocation" && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Employee *</label>
                <Select value={form.employee_id || ''} onValueChange={handleEmployeeSelect}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="team-employee"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 z-[200] max-h-[250px]">
                    {employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_id})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Role *</label>
                <Select value={form.role || ''} onValueChange={(v) => set('role', v)}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="team-role"><SelectValue placeholder="Select Role" /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                    {meta?.team_roles?.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start Date</label>
                <Input type="date" value={form.start_date || ''} onChange={(e) => set('start_date', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="team-start-date" />
              </div>
            </>
          )}

          {/* === SHOOT LOG === */}
          {entryType === "Shoot Log" && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Title *</label>
                <Input value={form.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Brand shoot — April batch" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Shoot Date *</label>
                  <Input type="date" value={form.shoot_date || ''} onChange={(e) => set('shoot_date', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-date" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Location</label>
                  <Input value={form.shoot_location || ''} onChange={(e) => set('shoot_location', e.target.value)} placeholder="Studio / On-site" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-location" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Raw Content Link</label>
                <Input value={form.raw_content_link || ''} onChange={(e) => set('raw_content_link', e.target.value)} placeholder="https://drive.google.com/..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-raw-link" />
              </div>
            </>
          )}

          {/* === GENERIC TYPES === */}
          {["Content & Assets", "Branding Files", "Performance / Ads Resources"].includes(entryType) && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Title *</label>
                <Input value={form.title || ''} onChange={(e) => set('title', e.target.value)} placeholder={entryType === "Content & Assets" ? "Content Calendar" : entryType === "Branding Files" ? "Logo Files" : "Campaign Dashboard"} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="generic-title" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Link</label>
                <Input value={form.link || ''} onChange={(e) => set('link', e.target.value)} placeholder="https://..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="generic-link" />
              </div>
            </>
          )}

          {/* === CLIENT FEEDBACK === */}
          {entryType === "Client Feedback / Communication" && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Title *</label>
                <Input value={form.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Meeting notes — April review" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="feedback-title" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date</label>
                <Input type="date" value={form.date || ''} onChange={(e) => set('date', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="feedback-date" />
              </div>
            </>
          )}

          {/* Notes (always) */}
          {entryType && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Notes</label>
              <Input value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Additional notes..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="entry-notes" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !canSubmit} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-entry-button">
            {loading ? 'Saving...' : 'Save Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
